/**
 * Copyright Mántum All Rights Reserved.
 */
import './style.css';

import Button from 'devextreme-react/button';
import CustomDetail, { type PropTypes as Detail } from './detail';
import CustomStore from 'devextreme/data/custom_store';
import LicenseKey from 'devextreme/core/config';
import Source from 'devextreme/data/data_source';
import debug from 'debug';
import esMessages from 'devextreme/localization/messages/es.json';
import fetcher from './fetcher';
import request from '@mantum/kanela-request';
import useSWR from 'swr';
import { locale, loadMessages } from 'devextreme/localization';
import { sort, source, validator, router } from '@mantum/kanela';

import {
    KEY_ALERT,
    KEY_CANCEL,
    KEY_COMPLETE,
    KEY_CONTENT,
    KEY_CURSOR,
    KEY_DATA,
    KEY_DATA_SOURCE,
    KEY_SELECTED_ROWS_DATA,
    KEY_SELECTED_ROWS_DATA_MASTER_DETAIL,
    KEY_SELECTED_ROWS_KEYS,
    KEY_TYPE_EMAIL,
    KEY_TYPE_SELECT
} from './constant';

import DataTable, {
    Button as Control,
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    ColumnChooserSelection,
    Editing,
    EmailRule,
    Export,
    FilterRow,
    GroupPanel,
    HeaderFilter,
    Item,
    LoadPanel,
    Lookup,
    MasterDetail,
    Pager,
    Paging,
    RequiredRule,
    RowDragging,
    Scrolling,
    SearchPanel,
    Selection,
    Sorting,
    StateStoring,
    Summary,
    Toolbar,
    TotalItem,
    DataGridTypes
} from 'devextreme-react/data-grid';

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef
} from 'react';

import type dxDataGrid from 'devextreme/ui/data_grid';
import type { EventInfo } from 'devextreme/events';

import type {
    Button as Action,
    Buttons as Actions,
    Columns,
    Column as Row,
    DataGrid as Component,
    DataSource,
    Result,
    Summaries
} from '@mantum/kanela';

import type {
    CellPreparedEvent,
    ColumnButtonClickEvent,
    ContextMenuPreparingEvent,
    ExportingEvent,
    RowPreparedEvent
} from 'devextreme/ui/data_grid';

const extractReg = /\{([^}]+)\}/g;

type Reorder = {
    component: {
        getVisibleRows: () => [];
    };
    toIndex: number;
    itemData: Record<string, unknown>;
};

type PropTypes = {
    advanced?: {
        alert?: string;
        allowAdding?: boolean;
        allowColumnChooser?: boolean;
        allowColumnHidingEnabled?: boolean;
        allowCustomize?: boolean;
        allowDeleting?: boolean;
        allowExporting?: boolean;
        allowFiltering?: boolean;
        allowPaging?: boolean;
        allowRefresh?: boolean;
        allowReordering?: boolean;
        allowSearch?: boolean;
        allowUpdating?: boolean;
        entity?: string;
        height?: number | string;
        mode?: 'infinite' | 'virtual';
        namespace?: string;
        refresh?: number;
        rememberSelection?: boolean;
        revalidate?: boolean;
        textColor?: string;
        selection?: 'none' | 'single' | 'multiple';
        storing?: boolean;
        summary?: {
            result?: Result;
        };
        hideDelete?: number[];
        defaultNoDataText?: string;
    };
    actions?: Actions;
    columns?: Columns;
    dataSource?: DataSource | unknown[];
    detail?: Detail;
    menu?: [number, string][];
    name?: string;
    onChange?: (name?: string, value?: unknown) => void;
    onClick?: (value: unknown) => void;
    onOrdered?: (moving: unknown, destination?: unknown) => void;
    onRightClick?: (key: number, value: unknown) => void;
    onRowAction?: (name?: string, value?: unknown) => void;
    onRowRemoved?: (value?: unknown) => void;
    onRowRemoving?: () => Promise<unknown>;
    readonly?: boolean;
    summaries?: Summaries;
    testid?: string;
};

LicenseKey({
    licenseKey: import.meta.env.VITE_DEVEXTREME_KEY ?? ''
});

const logger = debug('kanela:datagrid');

export default forwardRef<Component, PropTypes>(function DataGrid(props, ref) {
    const {
        actions = [],
        advanced = {},
        columns = [],
        dataSource = [],
        detail = {},
        menu = [],
        name,
        onChange,
        onClick,
        onRightClick,
        onOrdered,
        onRowAction,
        onRowRemoved,
        onRowRemoving,
        readonly = false,
        summaries = []
    } = props;

    const {
        alert = 'red',
        allowColumnChooser = true,
        allowColumnHidingEnabled = false,
        allowCustomize = false,
        allowFiltering = true,
        allowRefresh = true,
        allowReordering = false,
        allowSearch = true,
        entity,
        height = '340px',
        hideDelete = [],
        mode = 'standard',
        namespace = '',
        refresh = undefined,
        rememberSelection = true,
        revalidate = true,
        selection = 'single',
        storing = true,
        textColor = 'white',
        defaultNoDataText = undefined
    } = advanced;

    const isInfiniteScrolling = mode === 'infinite';
    const config = {
        refreshInterval: refresh,
        revalidateOnFocus: revalidate
    };

    // prettier-ignore
    const {
        enabled = false
    } = detail

    const changeRef = useRef(onChange);
    const orderedRef = useRef(onOrdered);
    const instanceRef = useRef<unknown>(null);

    const navigationRef = useRef({
        next: null,
        previous: null
    });

    const query = useMemo(() => {
        return !Array.isArray(dataSource) &&
            !validator.isUndefined(dataSource.url)
            ? {
                  url: dataSource.url,
                  args: {
                      body: dataSource.parameter,
                      headers: dataSource.headers,
                      method: dataSource.method,
                      namespace: dataSource.namespace,
                      pagination: dataSource.pagination
                  }
              }
            : null;
    }, [dataSource]);

    const { data, mutate } = useSWR(
        () => (!isInfiniteScrolling ? query : null),
        fetcher,
        config
    );

    const onRefresh = useCallback(() => {
        logger('refresh');
        mutate();
    }, [mutate]);

    const customStore = useMemo(() => {
        return new Source({
            key: entity,
            load: () => {
                return new Promise((resolve, reject) => {
                    if (!query) {
                        resolve([]);
                        return;
                    }

                    if (
                        navigationRef.current.next === null &&
                        navigationRef.current.previous !== null
                    ) {
                        resolve([]);
                        return;
                    }

                    const cursor = !navigationRef.current.next
                        ? null
                        : router.query(KEY_CURSOR, navigationRef.current.next);

                    const body = query.args.body || {};
                    if (cursor) {
                        body['cursor'] = cursor;
                    }

                    const response = request.fetch(query.url, {
                        method: query.args.method,
                        body,
                        headers: query.args.headers
                    });

                    response.then((values: unknown[]) => {
                        resolve(source.extract(values, query.args.namespace));
                        navigationRef.current = source.extract(
                            values,
                            query.args.pagination
                        );
                        logger('navigation %o', navigationRef.current);
                    });

                    response.catch((error: unknown) => {
                        reject(error);
                    });
                });
            }
        });
    }, [entity, query]);

    useEffect(() => {
        changeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        orderedRef.current = onOrdered;
    }, [onOrdered]);

    useEffect(() => {
        loadMessages(esMessages);
        if (typeof window !== 'undefined') {
            if (window.navigator && window.navigator.language) {
                locale(window.navigator.language);
            }
        }
    }, []);

    useImperativeHandle(
        ref,
        () => {
            return {
                name,

                clear() {
                    const current = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;
                    current?.option(KEY_DATA_SOURCE, []);
                },

                clearSelection() {
                    const current = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;

                    current?.clearSelection();
                },

                validate() {
                    const current = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;

                    const options = current?.option(KEY_DATA_SOURCE);
                    if (Array.isArray(options) && options.length === 0) {
                        return false;
                    }
                    return true;
                },

                refresh() {
                    onRefresh();
                },

                value() {
                    const current = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;

                    const values = current?.option(KEY_DATA_SOURCE);
                    if (Array.isArray(values)) {
                        return values;
                    }
                    return [];
                },

                getSelection() {
                    const current = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;
                    const values: unknown[] = [];

                    const rows = current?.option(KEY_SELECTED_ROWS_KEYS);
                    if (Array.isArray(rows) && rows.length) {
                        values.push(...rows);
                    }

                    const rowsMasterDetail = current?.option(
                        KEY_SELECTED_ROWS_DATA_MASTER_DETAIL
                    );
                    if (
                        Array.isArray(rowsMasterDetail) &&
                        rowsMasterDetail.length
                    ) {
                        values.push(...rowsMasterDetail);
                    }

                    return values;
                }
            };
        },
        [name, onRefresh]
    );

    const getLookupDataSource = useCallback((dataSource: DataSource) => {
        logger('lookup data source');
        return new Promise((resolve, reject) => {
            const response = request.fetch(dataSource.url, {
                body: dataSource.parameter,
                headers: dataSource.headers,
                method: dataSource.method
            });

            response.then((values: unknown[]) => {
                const results = source.extract(values, dataSource.namespace);
                if (Array.isArray(results)) {
                    resolve(results);
                    return;
                }
                resolve([]);
            });

            response.catch((error: unknown) => {
                reject(error);
            });
        });
    }, []);

    const getColumnDataSource = useCallback(
        (entity?: string, dataSource?: DataSource | unknown[]) => {
            logger('column data source');
            if (Array.isArray(dataSource)) {
                return dataSource;
            }

            return {
                store: new CustomStore({
                    key: entity || 'id',
                    loadMode: 'raw',
                    load() {
                        return getLookupDataSource(dataSource as DataSource);
                    }
                })
            };
        },
        [getLookupDataSource]
    );

    const getTotalSummaryValue = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            let values: Record<string, unknown> = {};
            summaries.forEach((it) => {
                const key = it.name;
                if (key) {
                    values[key] = event.component.getTotalSummaryValue(key);
                }
            });

            if (props.advanced?.summary?.result === KEY_COMPLETE) {
                values = {
                    summary: values
                };
            }
            return values;
        },
        [summaries, props.advanced?.summary?.result]
    );

    const handleRow = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            event.component
                ?.getDataSource()
                ?.store()
                ?.load()
                .then((result) => {
                    if (changeRef.current) {
                        changeRef.current(name, result);

                        const summaries = getTotalSummaryValue(event);
                        Object.keys(summaries).forEach((it) => {
                            if (changeRef.current) {
                                changeRef.current(it, summaries[it]);
                            }
                        });
                    }
                });
        },
        [name, getTotalSummaryValue]
    );

    const handleRowRemoved = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            logger('row removed');
            handleRow(event);
            if (onRowRemoved) {
                const data = KEY_DATA in event ? event?.[KEY_DATA] : undefined;
                onRowRemoved(data);
            }
        },
        [handleRow, onRowRemoved]
    );

    const handleRowRemoving = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            if (onRowRemoving && KEY_CANCEL in event) {
                logger('row removing');
                event.cancel = onRowRemoving();
            }
        },
        [onRowRemoving]
    );

    const onSelectionChanged = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            if (KEY_SELECTED_ROWS_DATA in event) {
                const selectedRowsData = event.selectedRowsData;
                const current = selectedRowsData;

                if (!Array.isArray(current) || current.length === 0) {
                    if (onClick) {
                        onClick([]);
                    }
                    return;
                }

                if (!rememberSelection) {
                    setTimeout(() => {
                        const instance = instanceRef.current as dxDataGrid<
                            unknown,
                            unknown
                        >;
                        instance?.clearSelection();
                    }, 500);
                }

                logger('selection changed');
                if (onClick) {
                    onClick(current);
                }
            }
        },
        [onClick, rememberSelection]
    );

    const onSelectionChangedDetail = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            if (KEY_SELECTED_ROWS_DATA in event) {
                const selectedRowsData = event.selectedRowsData;
                const current = selectedRowsData;

                if (!Array.isArray(current) || current.length === 0) {
                    return;
                }

                const instance = instanceRef.current as dxDataGrid<
                    unknown,
                    unknown
                >;
                instance?.option(KEY_SELECTED_ROWS_DATA_MASTER_DETAIL, current);
            }
        },
        []
    );

    const onContentReady = useCallback(
        (event: EventInfo<dxDataGrid<unknown, unknown>>) => {
            instanceRef.current = event.component;
            if (instanceRef.current) {
                logger('content ready');
                const current = instanceRef.current as dxDataGrid<
                    unknown,
                    unknown
                >;

                if (changeRef.current && !isInfiniteScrolling) {
                    const values = current.option(KEY_DATA_SOURCE);
                    changeRef.current(name, values);
                }
            }
        },
        [name, isInfiniteScrolling]
    );

    const onContextMenuPreparing = useCallback(
        (event: ContextMenuPreparingEvent) => {
            if (event.target === KEY_CONTENT) {
                if (!event.items) {
                    event.items = [];
                }

                menu.forEach((it) => {
                    const [key, name] = it;
                    event.items?.push({
                        text: name,
                        onItemClick: () => {
                            if (onRightClick) {
                                onRightClick(key, event.row?.data);
                            }
                        }
                    });
                });
            }
        },
        [menu, onRightClick]
    );

    const renderLinkCell = useCallback(
        (cell: CellPreparedEvent, link: string) => {
            // prettier-ignore
            const {
            data,
            value
        } = cell;

            const keys = link.match(extractReg);
            if (keys?.length === 0) {
                return;
            }

            keys?.forEach((key) => {
                const namespace = key.replaceAll('{', '').replaceAll('}', '');
                const value = source.extract(data, namespace);

                if (value !== undefined && value !== null) {
                    link = link.replaceAll(key, `${value}`);
                }
            });

            return (
                <a
                    className='hover:underline'
                    href={link}
                    rel='noreferrer'
                    target='_blank'
                >
                    {value}
                </a>
            );
        },
        []
    );

    const commonColumn = useCallback(
        (row: Row) => (
            <Column
                sortOrder={row.sort}
                allowEditing={!(readonly || (row.readonly ?? false))}
                allowHiding={row.allowHiding}
                caption={row.label}
                dataField={row.name}
                allowSorting={row.allowSorting}
                editorOptions={row.type === 'number' ? row.advanced : undefined}
                dataType={
                    row.type !== 'select' &&
                    row.type !== 'email' &&
                    row.type !== 'link'
                        ? row.type
                        : 'string'
                }
                format={row.format}
                groupIndex={row.group}
                key={row.name}
                visible={row.visible}
                cellRender={
                    row.type === 'link' && row.link
                        ? (cell) => renderLinkCell(cell, row.link!)
                        : undefined
                }
            >
                {!row.optional && (
                    <RequiredRule message={row.validation?.message} />
                )}

                {row.type === KEY_TYPE_EMAIL &&
                    row.validation?.email?.enabled && (
                        <EmailRule message={row.validation?.email.message} />
                    )}

                {row.type === KEY_TYPE_SELECT && (
                    <Lookup
                        dataSource={getColumnDataSource(
                            row.entity,
                            row.dataSource
                        )}
                        displayExpr={row.options?.label || 'label'}
                        valueExpr={row.options?.value || 'value'}
                    />
                )}
            </Column>
        ),
        [readonly, getColumnDataSource, renderLinkCell]
    );

    const nestedColumn = useCallback(
        (row: Row) => (
            <Column caption={row.label} key={row.name} alignment='center'>
                {row.children?.map((child) => commonColumn(child))}
            </Column>
        ),
        [commonColumn]
    );

    const rows = useMemo(() => {
        logger('prepare list rows');
        return columns
            .sort(sort.asc)
            .filter((row) => !row.hidden)
            .map((row) =>
                row.children ? nestedColumn(row) : commonColumn(row)
            );
    }, [columns, nestedColumn, commonColumn]);

    const DetailView = useCallback(
        (selected: Record<string, unknown>) => {
            // prettier-ignore
            const {
                data = {}
            } = selected

            // prettier-ignore
            const row = data && typeof data === 'object'
                ? data
                : null;

            let dataSource = detail.dataSource;
            if (dataSource) {
                if (typeof dataSource === 'string') {
                    const results = source.extract(row, dataSource);
                    if (Array.isArray(results)) {
                        dataSource = results;
                    }
                } else if (
                    typeof dataSource === 'object' &&
                    !Array.isArray(dataSource)
                ) {
                    if (row && 'url' in dataSource) {
                        dataSource = {
                            ...(detail.dataSource as DataSource)
                        };

                        let url = dataSource.url;
                        Object.keys(row).forEach((it) => {
                            url = url.replace(
                                `{${it}}`,
                                row[it as keyof unknown]
                            );
                        });
                        dataSource.url = url;
                    }
                }
            }

            logger('prepare detail');
            return (
                <CustomDetail
                    allowFiltering={detail.allowFiltering}
                    columns={detail.columns}
                    dataSource={dataSource}
                    onClick={onSelectionChangedDetail}
                    selected={selected}
                    selection={detail.selection}
                />
            );
        },
        [
            detail.columns,
            detail.dataSource,
            detail.allowFiltering,
            detail.selection,
            onSelectionChangedDetail
        ]
    );

    const onExporting = (event: ExportingEvent) => {
        const format = event.format;
        logger('export file %s', format);

        if (format === 'xlsx') {
            import('./xlsx').then((instance) => {
                const Workbook = instance.default;
                const workbook = new Workbook();
                const worksheet = workbook.addWorksheet('Main sheet');

                import('./xlsx-exporter')
                    .then((instance) => {
                        return instance.default({
                            component: event.component,
                            worksheet,
                            autoFilterEnabled: true,
                            customizeCell: ({ gridCell, excelCell }) => {
                                if (gridCell?.rowType === 'data') {
                                    const columnConfig = columns.find(
                                        (col) =>
                                            col.name ===
                                            gridCell?.column?.dataField
                                    );
                                    if (
                                        columnConfig &&
                                        columnConfig.type === 'link'
                                    ) {
                                        const linkTemplate = columnConfig.link;
                                        const linkValue = linkTemplate?.replace(
                                            `{${gridCell?.column?.dataField}}`,
                                            gridCell.value
                                        );
                                        excelCell.value = {
                                            text: gridCell.value,
                                            hyperlink: linkValue
                                        };
                                        excelCell.font = {
                                            color: { argb: 'ef6636' },
                                            underline: true
                                        };
                                        excelCell.alignment = {
                                            horizontal: 'left'
                                        };
                                    }
                                }
                            }
                        });
                    })
                    .then(() => {
                        import('./file-system').then((instance) => {
                            const saveAs = instance.default;
                            workbook.xlsx.writeBuffer().then((buffer) => {
                                saveAs(
                                    new Blob([buffer], {
                                        type: 'application/octet-stream'
                                    }),
                                    'DataGrid.xlsx'
                                );
                            });
                        });
                    });

                event.cancel = true;
            });
        } else {
            import('./pdf').then((instance) => {
                const doc = new instance.default();

                import('./pdf-exporter')
                    .then((instance) => {
                        return instance.default({
                            jsPDFDocument: doc,
                            component: event.component,
                            indent: 5
                        });
                    })
                    .then(() => {
                        doc.save('DataGrid.pdf');
                    });

                event.cancel = true;
            });
        }
    };

    const saveStateStoring = useCallback(
        (state: unknown) => {
            if (name && typeof state === 'object') {
                if (window.localStorage) {
                    const current = {
                        ...state,
                        selectedRowKeys: []
                    };

                    const key = !namespace ? name : `${namespace}/${name}`;
                    logger('save (%s) state storing %o', key, current);
                    window.localStorage.setItem(key, JSON.stringify(current));
                }
            }
        },
        [name, namespace]
    );

    const loadStateStoring = useCallback(() => {
        try {
            if (name && window.localStorage) {
                const key = !namespace ? name : `${namespace}/${name}`;
                const current = window.localStorage.getItem(key);
                if (current) {
                    const values = JSON.parse(current);
                    logger('load (%s) state storing %o', key, values);
                    return values;
                }
            }
            return null;
        } catch {
            return null;
        }
    }, [name, namespace]);

    const onReorder = useCallback(
        (event: Record<string, unknown>) => {
            if (!entity) {
                logger('the key is undefined');
                return;
            }

            const target = event as Reorder;
            const visibleRows = target.component.getVisibleRows();
            if (visibleRows.length === 0) {
                logger('the visible rows is empty');
                return;
            }

            const items = Array.isArray(dataSource) ? dataSource : data;
            if (!items || items.length === 0) {
                logger('the data is empty');
                return;
            }

            const toIndex = items.findIndex((item) => {
                const before =
                    item && typeof item === 'object' && entity in item
                        ? item?.[entity as keyof unknown]
                        : null;

                const row = visibleRows[target.toIndex] as Record<
                    string,
                    unknown
                >;

                const data =
                    row && typeof row === 'object' && 'data' in row
                        ? row.data
                        : null;

                const after =
                    data && typeof data === 'object' && 'id' in data
                        ? data?.[entity as keyof unknown]
                        : null;

                return before === after;
            });

            const fromIndex = items.findIndex((item) => {
                const before =
                    item && typeof item === 'object' && entity in item
                        ? item?.[entity as keyof unknown]
                        : null;

                return (
                    entity in target.itemData &&
                    before === target.itemData[entity]
                );
            });

            if (orderedRef.current) {
                const moving = target.itemData;
                if (moving) {
                    let index: number | null = toIndex;
                    if (fromIndex > toIndex) {
                        index = toIndex - 1;
                        if (index < 0) {
                            index = null;
                        }
                    }

                    const destination = index !== null ? items[index] : null;
                    orderedRef.current(moving, destination);
                }
            }

            items.splice(fromIndex, 1);
            items.splice(toIndex, 0, target.itemData);

            if (Array.isArray(dataSource)) {
                if (instanceRef.current) {
                    const instance = instanceRef.current as dxDataGrid<
                        unknown,
                        unknown
                    >;
                    instance?.option(KEY_DATA_SOURCE, items);
                }
            } else {
                mutate(items, false);
            }

            if (changeRef.current) {
                changeRef.current(name, items);
            }
        },
        [entity, dataSource, data, mutate, name]
    );

    const onHideDeleteAction = useCallback(
        (event: CellPreparedEvent<dxDataGrid<unknown, unknown>>) => {
            if (hideDelete.length === 0) {
                return;
            }

            const isValid =
                'command' in event.column && event.column.command === 'edit';

            if (event.rowType === 'data' && isValid) {
                if (hideDelete.includes(source.extract(event.data, entity))) {
                    if ('children' in event.cellElement) {
                        Array.from(event.cellElement.children).forEach((it) => {
                            if (it.classList.contains('dx-link-delete')) {
                                event.cellElement.removeChild(it);
                            }
                        });
                    }
                }
            }
        },
        [hideDelete, entity]
    );

    const customizeCells = useCallback(
        (event: CellPreparedEvent<dxDataGrid<unknown, unknown>>) => {
            onHideDeleteAction(event);
        },
        [onHideDeleteAction]
    );

    const onRowControl = useCallback(
        (
            event: ColumnButtonClickEvent<dxDataGrid<unknown, unknown>>,
            action: Action
        ) => {
            if (onRowAction) {
                onRowAction(action.name, event.row?.data);
            }
        },
        [onRowAction]
    );

    const editing =
        props.advanced?.allowUpdating &&
        !props.advanced?.allowAdding &&
        !props.advanced?.allowDeleting
            ? 'cell'
            : 'row';

    const allowAdding = !props.readonly && props.advanced?.allowAdding;
    const allowDeleting = !props.readonly && props.advanced?.allowDeleting;
    const allowUpdating = !props.readonly && props.advanced?.allowUpdating;

    const items = !Array.isArray(dataSource) ? data : dataSource;
    const store = isInfiniteScrolling ? customStore : items;
    const sorting = !isInfiniteScrolling ? 'single' : 'none';

    const isColumnChooser = !allowColumnChooser
        ? allowColumnChooser
        : !allowAdding && !allowDeleting;

    const isCustomize = allowCustomize
        ? `color: ${textColor}; background-color: ${alert}`
        : '';

    const onRowPrepared = (e: RowPreparedEvent) => {
        if (e.rowType === 'data') {
            if (KEY_ALERT in e.data && e.data?.[KEY_ALERT]) {
                e.rowElement.style.cssText = isCustomize;
            }
        }
    };

    const getConditionalProps = () => {
        const props: DataGridTypes.Properties = {};

        if (defaultNoDataText) {
            props.noDataText = defaultNoDataText;
        }

        return props;
    };

    return (
        <div className='w-full' data-testid={props.testid}>
            <DataTable
                elementAttr={{
                    'data-ready': instanceRef.current !== null
                }}
                allowColumnReordering={true}
                allowColumnResizing={true}
                cacheEnabled={true}
                columnAutoWidth={true}
                columnHidingEnabled={allowColumnHidingEnabled}
                dataSource={store}
                errorRowEnabled={false}
                height={height}
                keyExpr={!isInfiniteScrolling ? entity : ''}
                onCellPrepared={customizeCells}
                onContentReady={onContentReady}
                onContextMenuPreparing={onContextMenuPreparing}
                onExporting={onExporting}
                onRowInserted={handleRow}
                onRowPrepared={onRowPrepared}
                onRowRemoved={handleRowRemoved}
                onRowRemoving={handleRowRemoving}
                onRowUpdated={handleRow}
                onSelectionChanged={onSelectionChanged}
                remoteOperations={isInfiniteScrolling}
                showBorders={true}
                width='100%'
                wordWrapEnabled={true}
                {...getConditionalProps()}
            >
                <Scrolling
                    columnRenderingMode='standard'
                    mode={mode}
                    rowRenderingMode='standard'
                />
                <Sorting mode={sorting} />
                <GroupPanel visible={!isInfiniteScrolling} />
                <SearchPanel
                    visible={!isInfiniteScrolling ? allowSearch : false}
                />
                <Paging
                    defaultPageSize={50}
                    enabled={props.advanced?.allowPaging}
                />
                <Pager
                    visible={
                        !isInfiniteScrolling && props.advanced?.allowPaging
                    }
                    allowedPageSizes={[10, 25, 50]}
                    displayMode='full'
                    showPageSizeSelector={true}
                    showInfo={false}
                    showNavigationButtons={false}
                />
                <ColumnChooser enabled={isColumnChooser} mode='select'>
                    <ColumnChooserSearch enabled={true} />
                    <ColumnChooserSelection
                        allowSelectAll={true}
                        selectByClick={true}
                        recursive={true}
                    />
                </ColumnChooser>
                <RowDragging
                    allowReordering={allowReordering}
                    onReorder={onReorder}
                    showDragIcons={true}
                />
                <HeaderFilter
                    search={{ enabled: true }}
                    visible={allowFiltering}
                />
                <FilterRow visible={allowFiltering} />
                <LoadPanel enabled={true} />
                <Selection mode={selection} showCheckBoxesMode='always' />
                <Export
                    allowExportSelectedData={selection === 'multiple'}
                    enabled={props.advanced?.allowExporting}
                    formats={['pdf', 'xlsx']}
                />
                <Toolbar>
                    <Item name='addRowButton' />
                    {allowRefresh && !isInfiniteScrolling && (
                        <Item location='after'>
                            <Button icon='refresh' onClick={onRefresh} />
                        </Item>
                    )}
                    <Item name='exportButton' />
                    {isColumnChooser && <Item name='columnChooserButton' />}
                    {!isInfiniteScrolling && allowSearch && (
                        <Item name='searchPanel' />
                    )}
                </Toolbar>
                <StateStoring
                    customLoad={loadStateStoring}
                    customSave={saveStateStoring}
                    enabled={storing}
                    type='custom'
                />
                {summaries.length > 0 && (
                    <Summary>
                        {summaries.map((summary) => (
                            <TotalItem
                                column={summary.column}
                                displayFormat={summary.format}
                                key={summary.column}
                                name={summary.name}
                                summaryType={summary.type}
                                valueFormat={summary.formatValue}
                            />
                        ))}
                    </Summary>
                )}
                <Editing
                    mode={editing}
                    allowAdding={allowAdding}
                    allowDeleting={allowDeleting}
                    allowUpdating={allowUpdating}
                />

                {rows}

                {((editing === 'row' && allowUpdating) ||
                    allowDeleting ||
                    actions.length > 0) && (
                    <Column
                        allowHiding={false}
                        showInColumnChooser={false}
                        type='buttons'
                    >
                        <Control name='edit' />
                        <Control name='delete' />

                        {actions.sort(sort.asc).map((row) => (
                            <Control
                                disabled={row.readonly}
                                hint={row.label}
                                key={row.name}
                                onClick={(
                                    event: ColumnButtonClickEvent<
                                        dxDataGrid<unknown, unknown>
                                    >
                                ) => onRowControl(event, row)}
                                text={row.label}
                            />
                        ))}
                    </Column>
                )}

                <MasterDetail enabled={enabled} render={DetailView} />
            </DataTable>
        </div>
    );
});
